import moment from 'moment';
import React, { useContext, useMemo, useRef, useState } from 'react';
import { SearchBarCenter } from '@/common/styles/SearchBars';
import { useCommonDataContext } from '@/common/hooks/commonDataContext';
import { useRequestsContext } from '@/common/hooks/requestHook';
import { getConsultantsDailyReport } from '@/API/repositories/reports';
import Loading from '@/common/components/Loading';
import Input from '@/common/components/Input';
import { Colors } from '@/common/colors/colors';
import SelectInput from '@/common/components/SelectInput';
import { handleMapToOptions } from '@/common/functions/handleMapToOptions';
import { WorkingByTake } from '@/common/components/workingByTake/WorkingByTake';
import NoResponseShiftTable from '@/common/components/noResponseShiftTable/NoResponseShiftTable';
import DownloadTable from '@/common/components/downloadTable/DownloadTable';
import {
	ActionsRow,
	BarBody,
	ConsultantsColumn,
	FiltersRow,
	Wrapper,
} from './DailyReportManager.styled';
import { useMessageQueueContext } from '@/common/hooks/useMessageQueue';
import ActionButton from '@/common/components/buttons/ActionButton';
import { AuthContext } from '@/common/contexts/authContext';
import { findSuperior } from '@/common/functions/findSuperior';
import { formatToDateTamplate } from '@/common/functions/dateFormater';
import { isReportRangeLongerThanMonth } from '@/common/functions/isReportRangeLongerThanMonth';
import TeamLeaderProductSelect, {
	useTeamSuperProductFilter,
} from '@/common/components/TeamLeaderProductSelect';

const groupHistoriesByProduct = (consultantsData, field) => {
	const groups = new Map();

	consultantsData.forEach((consultantData) => {
		(consultantData[field] || []).forEach((call) => {
			const productId = call._order?.product || '';
			const key = `${call.consultant}_${productId}`;

			if (!groups.has(key)) {
				groups.set(key, []);
			}

			groups.get(key).push(call);
		});
	});

	return Array.from(groups.values());
};

const DailyReportManager = () => {
	const { authUser } = useContext(AuthContext);

	const [selectedConsultant, setSelectedConsultant] = useState([]);
	const [selectedProduct, setSelectedProduct] = useState(null);
	const [data, setData] = useState();
	const today = formatToDateTamplate(moment());

	const { commonData } = useCommonDataContext();
	const { users } = commonData;

	const { hasUnfilledRequest, makeRequest } = useRequestsContext();
	const { getProductIds } = useTeamSuperProductFilter();
	const { addMessage } = useMessageQueueContext();

	const dateFromRef = useRef();
	const dateToref = useRef();

	const currentUserId = authUser.id;

	const consultants = useMemo(() => {
		return handleMapToOptions(
			users?.filter(
				(user) =>
					findSuperior(user._id, users)?._id === currentUserId && user.active,
			) || [],
			'username',
			'_id',
		);
	}, [users, currentUserId]);

	const hanldeSearch = async (e) => {
		e && e.preventDefault();

		if (!selectedConsultant.length) {
			addMessage('Select consultants', 'error');

			return;
		}

		const today = moment();

		const payload = {};
		payload.from = dateFromRef.current?.value || today;
		payload.to = dateToref.current?.value || today;
		payload.consultants = selectedConsultant.map(
			(consultant) => consultant.value,
		);

		if (selectedProduct?.value) {
			const productIds = getProductIds(selectedProduct.value);

			if (!productIds.length) {
				addMessage('Not found');
				setData(() => null);
				return;
			}

			payload.products = productIds;
		}

		const isRangeTooWide = isReportRangeLongerThanMonth(
			payload.from,
			payload.to,
		);

		if (isRangeTooWide) {
			addMessage('Date range can be at most one month', 'error');
			return;
		}

		payload.from = moment(payload.from).startOf('date').toISOString();
		payload.to = moment(payload.to).endOf('date').toISOString();

		const response = await makeRequest(
			getConsultantsDailyReport.bind(null, payload),
		);

		if (response.data && !response.data.length) {
			addMessage('Not found');
			setData(() => null);

			return;
		}

		if (
			response.data &&
			response.data.length &&
			!response.data.some((element) => {
				return element._call_history.length || element._inbound_history.length;
			})
		) {
			addMessage('Not found');
			setData(() => null);

			return;
		}

		if (response.data) {
			setData(() => response.data);
		}
	};

	return (
		<Wrapper>
			{hasUnfilledRequest(getConsultantsDailyReport) && <Loading />}
			<SearchBarCenter>
				<BarBody>
					<FiltersRow>
						<div>
							<Input
								name='Download from'
								width={140}
								color={Colors.darkBlue}
								inputRef={dateFromRef}
								type='date'
								inputWidth={140}
								value={today}
							/>
							<Input
								name='Download to'
								width={140}
								color={Colors.darkBlue}
								inputRef={dateToref}
								type='date'
								inputWidth={140}
								value={today}
							/>
						</div>
						<ConsultantsColumn>
							<TeamLeaderProductSelect
								selected={selectedProduct}
								setSelected={setSelectedProduct}
								width={150}
								selectWidth={260}
								margin='0'
							/>
							<SelectInput
								name='Consultants'
								options={consultants}
								color={Colors.darkBlue}
								width={150}
								selectWidth={260}
								margin='0'
								setSelected={setSelectedConsultant}
								selected={selectedConsultant}
								multiple={true}
							/>
						</ConsultantsColumn>
					</FiltersRow>
					<ActionsRow>
						<ActionButton
							defaultText='Search'
							onClick={(e) => hanldeSearch(e)}
						/>
						<ActionButton
							defaultText='Add All'
							onClick={() => setSelectedConsultant(consultants)}
						/>
					</ActionsRow>
				</BarBody>
			</SearchBarCenter>
			{data && data.length > 0 && (
				<DownloadTable
					data={groupHistoriesByProduct(data, '_call_history')}
					consultants={users}
					name={'Outbound'}
					showProduct
				/>
			)}
			{data && data.length > 0 && (
				<DownloadTable
					data={groupHistoriesByProduct(data, '_inbound_history')}
					consultants={users}
					name={'Inbound'}
					showProduct
				/>
			)}
			{data && data.length > 0 && (
				<NoResponseShiftTable
					data={groupHistoriesByProduct(data, '_call_history')}
					consultants={users}
					name={'Outbound'}
					showProduct
				/>
			)}
			{data && data.length > 0 && (
				<WorkingByTake
					data={groupHistoriesByProduct(data, '_call_history')}
					consultants={users}
					name={'Outbound'}
					showProduct
				/>
			)}
		</Wrapper>
	);
};

export default DailyReportManager;
